import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { loadGameableForGass } from '../lib/gameableCharacterLoader.js'

export default function GameableAvatarModel({ character, onLoaded, onError }) {
  const { gl, camera, scene } = useThree()
  const model = useRef(null)
  const callbacks = useRef({ onLoaded, onError })
  callbacks.current = { onLoaded, onError }

  useFrame((_, delta) => model.current?.update(delta, camera))

  useEffect(() => {
    const controller = new AbortController()
    let disposed = false
    let loaded = null
    const load = async () => {
      // R3F normally initializes it; await init also covers a still-starting renderer.
      await gl.init?.()
      if (disposed) return
      loaded = await loadGameableForGass(gl, character, { signal: controller.signal })
      // Cancellation may race a GPU build that has already finished.
      if (disposed) { loaded.dispose(); return }
      scene.add(loaded.object3D)
      model.current = loaded
      callbacks.current.onLoaded?.({ bodyMeshLoaded: true, faceMeshLoaded: true })
    }
    load().catch(error => {
      if (!disposed) callbacks.current.onError?.(error)
    })
    return () => {
      disposed = true
      controller.abort()
      model.current = null
      if (loaded) {
        scene.remove(loaded.object3D)
        loaded.dispose()
      }
    }
    // By id: App re-spreads the same character on renames and probes, which
    // must not rebuild a 266k-splat character.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gl, scene, character?.id])

  return null
}
